import React, { useCallback, useState } from 'react'
import { Redirect, Route, Switch, useLocation } from 'react-router-dom'
import Header from 'components/Header/Header'
import Navigation from 'components/Navigation/Navigation'
import SettingsModal from 'components/SettingsModal/SettingsModal'
import Loader from 'components/Loader/Loader'
import Alerts from 'containers/Alerts/Alerts'
import DashboardPage from 'containers/DashboardPage/Loadable'
import BusinessesPage from 'containers/BusinessesPage/Loadable'
import RentalsPage from 'containers/Rentals/Rentals'
import OrganizationsPage from 'containers/OrganizationsPage/Loadable'
import BusinessPage from 'containers/BusinessPage/Loadable'
import InfluencersTablePage from 'containers/InfluencersTablePage/InfluencersTablePage'
import EcommerceTablePage from 'containers/EcommerceTablePage/EcommerceTablePage'
import FreelancersTablePage from 'containers/FreelancersTablePage/FreelancersTablePage'
import NFTTablePage from 'containers/NFTTablePage/NFTTablePage'
import CDTFATablePage from 'containers/CDTFATablePage/CDTFATablePage'
import OffshoreTablePage from 'containers/OffshoreTablePage/OffshoreTablePage'
import FatcaTablePage from 'containers/FatcaTablePage/FatcaTablePage'
import ServiceProvidersTablePage from 'containers/ServiceProvidersTablePage/ServiceProvidersTablePage'
import InfluencersVerticalPage from 'containers/VerticalPage/influencers/VerticalPage/VerticalPage'
import EcommerceVerticalPage from 'containers/VerticalPage/ecommerce/VerticalPage/VerticalPage'
import EcommerceExtendedVerticalPage from 'containers/VerticalPage/ecommerce-extended/VerticalPage/VerticalPage'
import STRVerticalPage from 'containers/VerticalPage/str/VerticalPage/VerticalPage'
import FreelancersVerticalPage from 'containers/VerticalPage/freelancers/VerticalPage/VerticalPage'
import NFTVerticalPage from 'containers/VerticalPage/nft/VerticalPage/VerticalPage'
import CDTFAVerticalPage from 'containers/VerticalPage/cdtfa/VerticalPage/VerticalPage'
import CompanyOverviewVerticalPage from 'containers/VerticalPage/company-overview/VerticalPage/VerticalPage'
import ServiceProvidersVerticalPage from 'containers/VerticalPage/serviceProviders/VerticalPage/VerticalPage'
import STRTablePage from 'containers/STRTablePage/STRTablePage'
import STRAdminPage from 'containers/STRAdminPage/STRAdminPage'
import EcommerceExtendedTablePage from 'containers/EcommerceExtendedTablePage/EcommerceExtendedTablePage'
import CompanyOverviewTablePage from 'containers/CompanyOverviewTablePage/CompanyOverviewTablePage'
import NexusVerticalPage from 'containers/VerticalPage/nexus/VerticalPage/VerticalPage'
import NexusTablePage from 'containers/NexusTablePage/NexusTablePage'

import { selectIsGlobalLoading, selectIsLoggingIn } from 'store/global'
import { selectIsModalDisplayed } from 'store/settings'
import VerticalContextProvider from 'store/verticals'

import { createTheme, ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { useSelector } from 'react-redux'
import cx from 'classnames'

import { globalTheme } from 'styles/globalTheme'
import styles from './App.scss'
import { SelectedRowsProvider } from 'store/selectedRows/SelectedRowsContext'
import ErrorBoundary from 'utils/ErrorBoundary/ErrorBoundary'
import { CurrencyProvider } from 'contexts'
import messages from './messages'
import { VerticalAccessGuard } from '../../components/VerticalAccessGuard/VerticalAccessGuard'
import { InjectedIntlProps, injectIntl, IntlShape } from 'react-intl'

interface ZoneIndexes {
  [key: string]: any
}
interface IntlProps {
  intl: IntlShape
}
function Portal({ intl }: IntlProps) {
  const location = useLocation()
  const { pathname = '' } = location || {}
  const isGlobalLoading = useSelector(selectIsGlobalLoading)
  const isLoggingIn = useSelector(selectIsLoggingIn)
  const isSettingsModalDisplayed = useSelector(selectIsModalDisplayed)

  const [currentZoneIndexes, setCurrentZoneIndexes] = useState<ZoneIndexes>({})

  const changeCurrentZoneIndexes = useCallback((newIndexes: ZoneIndexes) => {
    setCurrentZoneIndexes(newIndexes)
  }, [])

  const theme = createTheme(globalTheme)

  return (
    <CurrencyProvider>
      <Alerts />
      <VerticalContextProvider>
        <Header currentZoneIndexes={currentZoneIndexes} />
      </VerticalContextProvider>
      <Navigation />
      <main
        id='page'
        className={cx(
          styles.page,
          { [styles.newPage]: location.pathname.includes('/vertical') },
          styles[pathname.substring(1).split('/')[0]],
          { [styles.crypto]: pathname.includes('/crypto') },
        )}
      >
        <ErrorBoundary>
          <VerticalAccessGuard>
            <Switch>
              <Route exact path='/'>
                <Redirect to='/dashboard' />
              </Route>
              <Route exact path='/dashboard'>
                <DashboardPage />
              </Route>
              <Route path='/management'>
                <OrganizationsPage />
              </Route>
              <Route exact path='/businesses'>
                <BusinessesPage />
              </Route>
              <Route path='/businesses/:businessId'>
                <BusinessPage />
              </Route>
              <Route path='/singleBusiness/:businessId'>
                <BusinessPage />
              </Route>
              <Route path='/rentals'>
                <RentalsPage />
              </Route>
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <Route path='/vertical/influencers/:entityId'>
                  <VerticalContextProvider>
                    <InfluencersVerticalPage changeCurrentZoneIndexes={changeCurrentZoneIndexes} />
                  </VerticalContextProvider>
                </Route>
                <Route path='/vertical/ecommerce/:entityId'>
                  <VerticalContextProvider>
                    <EcommerceVerticalPage changeCurrentZoneIndexes={changeCurrentZoneIndexes} />
                  </VerticalContextProvider>
                </Route>
                <Route path='/vertical/freelancers/:entityId'>
                  <VerticalContextProvider>
                    <FreelancersVerticalPage changeCurrentZoneIndexes={changeCurrentZoneIndexes} />
                  </VerticalContextProvider>
                </Route>
                <Route path='/vertical/service-providers/:entityId'>
                  <VerticalContextProvider>
                    <ServiceProvidersVerticalPage changeCurrentZoneIndexes={changeCurrentZoneIndexes} />
                  </VerticalContextProvider>
                </Route>
                <Route path='/vertical/data-science/:entityId'>
                  <VerticalContextProvider>
                    <CDTFAVerticalPage changeCurrentZoneIndexes={changeCurrentZoneIndexes} />
                  </VerticalContextProvider>
                </Route>
                <Route path='/vertical/company-overview/:caseId'>
                  <VerticalContextProvider>
                    <CompanyOverviewVerticalPage changeCurrentZoneIndexes={changeCurrentZoneIndexes} />
                  </VerticalContextProvider>
                </Route>
                <Route path='/vertical/crypto/:entityId'>
                  <VerticalContextProvider>
                    <NFTVerticalPage changeCurrentZoneIndexes={changeCurrentZoneIndexes} />
                  </VerticalContextProvider>
                </Route>

                <Route path='/vertical/short-term-rentals/:entityId'>
                  <VerticalContextProvider>
                    <STRVerticalPage changeCurrentZoneIndexes={changeCurrentZoneIndexes} />
                  </VerticalContextProvider>
                </Route>

                <Route path='/vertical/ecommerce-extended/:entityId'>
                  <VerticalContextProvider>
                    <EcommerceExtendedVerticalPage changeCurrentZoneIndexes={changeCurrentZoneIndexes} />
                  </VerticalContextProvider>
                </Route>
                <Route path='/vertical/nexus/:caseId'>
                  <VerticalContextProvider>
                    <NexusVerticalPage changeCurrentZoneIndexes={changeCurrentZoneIndexes} />
                  </VerticalContextProvider>
                </Route>

                <Route path='/influencers'>
                  <SelectedRowsProvider>
                    <InfluencersTablePage />
                  </SelectedRowsProvider>
                </Route>
                <Route path='/ecommerce'>
                  <SelectedRowsProvider>
                    <EcommerceTablePage />
                  </SelectedRowsProvider>
                </Route>
                <Route path='/freelancers'>
                  <SelectedRowsProvider>
                    <FreelancersTablePage />
                  </SelectedRowsProvider>
                </Route>
                <Route path='/service-providers'>
                  <SelectedRowsProvider>
                    <ServiceProvidersTablePage />
                  </SelectedRowsProvider>
                </Route>
                <Route path='/nexus'>
                  <SelectedRowsProvider>
                    <NexusTablePage />
                  </SelectedRowsProvider>
                </Route>
                <Route path='/crypto'>
                  <SelectedRowsProvider>
                    <NFTTablePage />
                  </SelectedRowsProvider>
                </Route>
                <Route path='/data-science'>
                  <SelectedRowsProvider>
                    <CDTFATablePage />
                  </SelectedRowsProvider>
                </Route>
                <Route path='/offshore'>
                  <SelectedRowsProvider>
                    <OffshoreTablePage />
                  </SelectedRowsProvider>
                </Route>
                <Route path='/company-overview'>
                  <SelectedRowsProvider>
                    <CompanyOverviewTablePage />
                  </SelectedRowsProvider>
                </Route>
                <Route path='/fatca'>
                  <SelectedRowsProvider>
                    <FatcaTablePage />
                  </SelectedRowsProvider>
                </Route>

                <Route path='/ecommerce-extended'>
                  <SelectedRowsProvider>
                    <EcommerceExtendedTablePage />
                  </SelectedRowsProvider>
                </Route>

                <Route path='/str-admin'>
                  <STRAdminPage />
                </Route>
                <Route path='/short-term-rentals'>
                  <SelectedRowsProvider>
                    <STRTablePage />
                  </SelectedRowsProvider>
                </Route>
              </ThemeProvider>
            </Switch>
          </VerticalAccessGuard>
        </ErrorBoundary>
      </main>
      {isGlobalLoading && (
        // @ts-expect-error linter demands a message.
        <Loader type='globalLoading' message={isLoggingIn ? intl.formatMessage(messages.loggingIn) : ''} />
      )}
      {isSettingsModalDisplayed && <SettingsModal />}
    </CurrencyProvider>
  )
}

export default injectIntl(Portal as React.ComponentType<IntlProps & InjectedIntlProps>)
